import { filter, fromEvent, map, mergeMap, of, race, retry, takeUntil, tap } from 'rxjs';

export function createDragObservable(selector: string, exclude: string[], onComplete: () => void) {
  let startX = 0;
  let startY = 0;

  if (!selector) throw new Error('No Selector Identified');

  const element$ = of(selector).pipe(map(getElement), retry({ count: 10, delay: 250 }));

  const mousedown$ = element$.pipe(
    mergeMap((el) =>
      fromEvent<MouseEvent>(el, 'mousedown').pipe(
        filter((e) => !targetIsExcluded(exclude, e.target)),
        tap((e) => {
          startX = e.screenX;
          startY = e.screenY;
        })
      )
    )
  );

  const mouseup$ = fromEvent<MouseEvent>(document, 'mouseup').pipe(
    tap(() => {
      onComplete();
      startX = 0;
      startY = 0;
    })
  );

  const mousemove$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
    map((e) => ({ x: e.screenX - startX, y: e.screenY - startY })),
    takeUntil(mouseup$)
  );

  const touchstart$ = element$.pipe(
    mergeMap((e) =>
      fromEvent<React.TouchEvent>(e, 'touchstart').pipe(
        filter((e) => !targetIsExcluded(exclude, e.target)),
        tap((e) => {
          startX = e.touches[0].screenX;
          startY = e.touches[0].screenY;
        })
      )
    )
  );

  const touchend$ = fromEvent<React.TouchEvent>(document, 'touchend').pipe(
    tap(() => {
      onComplete();
      startX = 0;
      startY = 0;
    })
  );

  const touchmove$ = fromEvent<React.TouchEvent>(document, 'touchmove').pipe(
    map((e) => {
      const touch = e.touches[0];
      return { x: touch.screenX - startX, y: touch.screenY - startY };
    }),
    takeUntil(touchend$)
  );

  const mouses = mousedown$.pipe(mergeMap(() => mousemove$));
  const touches = touchstart$.pipe(mergeMap(() => touchmove$));

  return race(mouses, touches);
}

function getElement(s: string) {
  const el = document.querySelector(s);
  if (!el) throw new Error('No Draggable Element');

  return el;
}

function targetIsExcluded(excludeSelectors: string[], target: EventTarget | null) {
  if (excludeSelectors.length === 0) return false;

  const excludeEls = Array.from(document.querySelectorAll(excludeSelectors.join(',')));
  return excludeEls.some((el) => target instanceof Element && el.contains(target));
}
